import { Group, Stack } from '@mantine/core';
import { CheckboxButton, If, VLazyLoadMultiSelect, VSelectOptionWithDescription } from '@vision/ui/components';
import { ReportChartFeedbackBaseType } from '@vision/ui/enums';
import { useEmojiContext, useSelectedAccountId } from '@vision/ui/hooks';
import { ReportsDetailGraphicSchemaValues, SimpleFlowQuestion } from '@vision/ui/interfaces';
import {
  useLazyFetchFlowQuestionsWithPaginationQuery,
  useLazyFetchFlowsWithPaginationQuery,
} from '@vision/ui/services';
import { getBodyText, isObjectNotEmpty, translateErrorMessage } from '@vision/ui/utils';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

export function ReportsDetailGraphicFeedbackBasedQuestionBased() {
  const { t, i18n } = useTranslation(['page-reports', 'translation']);
  const accountId = useSelectedAccountId();
  const { textWithNativeEmoji } = useEmojiContext();

  const formik = useFormikContext<ReportsDetailGraphicSchemaValues>();

  const handleChangeType = (value: ReportChartFeedbackBaseType) => {
    formik.setValues({
      ...formik.values,
      selectedFeedbackBasedQuestionBased: value,
      selectedCompareFlowIds: [],
      selectedCompareQuestionIds: [],
      selectedCompareQuestionStatementIds: [],
    });
  };

  const handleDefaultValuesLoaded = (data: SimpleFlowQuestion[]) => {
    const newIds = formik.values.selectedCompareQuestionIds.filter((id) => data.some((item) => item.id === id));
    formik.setFieldValue('selectedCompareQuestionIds', newIds);
  };

  return (
    <>
      <Group justify="space-between" gap={10} pb={10} grow={true}>
        <CheckboxButton
          key={ReportChartFeedbackBaseType.FEEDBACK_BASED}
          label={t('feedbackBased')}
          grow={true}
          checked={formik.values.selectedFeedbackBasedQuestionBased === ReportChartFeedbackBaseType.FEEDBACK_BASED}
          onChange={() => handleChangeType(ReportChartFeedbackBaseType.FEEDBACK_BASED)}
        />
        <CheckboxButton
          key={ReportChartFeedbackBaseType.QUESTION_BASED}
          grow={true}
          label={t('questionBased')}
          checked={formik.values.selectedFeedbackBasedQuestionBased === ReportChartFeedbackBaseType.QUESTION_BASED}
          onChange={() => handleChangeType(ReportChartFeedbackBaseType.QUESTION_BASED)}
        />
      </Group>

      <If value={formik.values.selectedFeedbackBasedQuestionBased === ReportChartFeedbackBaseType.QUESTION_BASED}>
        <Stack gap={10}>
          <VLazyLoadMultiSelect
            id="report-feedback-base-type-flow"
            data-testid="input-report-feedback-base-type-flow"
            label={t('translation:flow')}
            useLazyApiQueryFunction={useLazyFetchFlowsWithPaginationQuery}
            apiRequestParameters={{ nodeId: accountId, sortBy: 'name', sortOrder: 'asc' }}
            multiSelectItemMapper={(items) => {
              return items.map((item) => ({
                value: item.id,
                label: item.name,
              }));
            }}
            searchKey="flow_name"
            value={formik.values.selectedCompareFlowIds}
            searchable={true}
            clearable={true}
            onBlur={formik.handleBlur}
            onChange={(values) => {
              formik.setFieldValue('selectedCompareFlowIds', values);
              formik.setFieldValue('selectedCompareQuestionIds', []);
              formik.setFieldValue('selectedCompareQuestionStatementIds', []);
            }}
            error={translateErrorMessage(formik, 'selectedCompareFlowIds')}
            withAsterisk={true}
            itemComponent={VSelectOptionWithDescription}
          />
          <If value={formik.values.selectedCompareFlowIds.length > 0}>
            <VLazyLoadMultiSelect
              id="report-compare-based-flow-question"
              data-testid="input-report-compare-based-flow-question"
              label={t('translation:question')}
              useLazyApiQueryFunction={useLazyFetchFlowQuestionsWithPaginationQuery}
              apiRequestParameters={{
                nodeId: accountId,
                flowIds: formik.values.selectedCompareFlowIds,
                sortBy: 'name',
                sortOrder: 'asc',
              }}
              multiSelectItemMapper={(items) => {
                return items
                  .filter((item) => isObjectNotEmpty(item.body))
                  .map((item) => ({
                    value: item.id,
                    label: textWithNativeEmoji(getBodyText(item.body, i18n.language)),
                    description: item?.flow_name,
                  }));
              }}
              searchKey="question_body"
              searchable={true}
              withAsterisk={true}
              value={formik.values.selectedCompareQuestionIds}
              onBlur={formik.handleBlur}
              onChange={(e) => {
                formik.setFieldValue('selectedCompareQuestionIds', e);
                formik.setFieldValue('selectedCompareQuestionStatementIds', []);
              }}
              error={translateErrorMessage(formik, 'selectedCompareQuestionIds')}
              resetChangeRequestParameters={true}
              onDefaultValuesLoaded={handleDefaultValuesLoaded}
              itemComponent={VSelectOptionWithDescription}
            />
          </If>
        </Stack>
      </If>
    </>
  );
}
